import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react';
import type {
  IntegrationData,
  SendToIntegrationContainerData,
  Template
} from '../types';
import {
  prepareUIFields,
  type UIFields
} from '../utils/prepareSendIssueFields';
import { ProvideValues } from '../components/steps/ProvideValues';
import { SelectTemplate } from '../components/steps/SelectTemplate';
import { useTranslation } from 'react-i18next';
import useIntegrationProjectsFromTemplates from './useIntegrationProjectsFromTemplates';

type useSendToIntegrationModalStepsProps = {
  selectedIntegrationData: IntegrationData | null;
  sendToIntegrationContainerData: SendToIntegrationContainerData;
  apiHostname: string;
  authFn: () => Promise<string>;
  userEnterpriseId: string;
  selectedTemplate?: Template;
  setSelectedTemplate: Dispatch<SetStateAction<Template | undefined>>;
  uiFields?: UIFields;
  setUIFields: Dispatch<SetStateAction<UIFields | undefined>>;
};

export const useSendToIntegrationModalSteps = ({
  sendToIntegrationContainerData,
  selectedIntegrationData,
  apiHostname,
  authFn,
  userEnterpriseId,
  selectedTemplate,
  setSelectedTemplate,
  uiFields,
  setUIFields
}: useSendToIntegrationModalStepsProps) => {
  const { t } = useTranslation();

  const [templateError, setTemplateError] = useState<string>();
  const [connectionIdsWithLoadingErrors, setConnectionIdsWithLoadingErrors] =
    useState<string[]>([]);

  const { isProjectsLoading, projectsByConnections, projectsError } =
    useIntegrationProjectsFromTemplates({
      activeConnections: selectedIntegrationData?.activeConnections || [],
      enterpriseId: userEnterpriseId,
      apiHostname,
      authFn,
      setConnectionIdsWithLoadingErrors
    });

  const isSelectTemplateStepValid = useCallback(() => {
    if (!selectedTemplate) {
      setTemplateError(t('Please select a template'));
      return false;
    }

    setTemplateError('');
    return true;
  }, [selectedTemplate]);

  useEffect(() => {
    if (!selectedTemplate) {
      return;
    }

    const preparedUiFields = prepareUIFields(
      selectedTemplate,
      sendToIntegrationContainerData.issues[0]
    );

    setUIFields(preparedUiFields);
  }, [selectedTemplate, sendToIntegrationContainerData.issues[0]]);

  const isProvideValuesStepValid = useCallback(() => {
    if (!uiFields) {
      return false;
    }

    let hasAnError = false;
    for (const uiField of uiFields.requiredFields) {
      const { integrationValue, error: integrationError } =
        uiField.mapper.convertDisplayToIntegrationValue(uiField);

      if (integrationError) {
        hasAnError = true;

        uiField.error = integrationError;
      } else {
        uiField.integrationValue = integrationValue;
      }
    }

    for (const uiField of uiFields.nonRequiredFields) {
      const { integrationValue, error: integrationError } =
        uiField.mapper.convertDisplayToIntegrationValue(uiField);

      if (integrationError) {
        hasAnError = true;

        uiField.error = integrationError;
      } else {
        uiField.integrationValue = integrationValue;
      }
    }

    if (hasAnError) {
      setUIFields({ ...uiFields });
    }

    return !hasAnError;
  }, [uiFields]);

  const steps = selectedIntegrationData
    ? [
        {
          isValid: isSelectTemplateStepValid,
          heading: t('Choose template'),
          content: (
            <SelectTemplate
              multiple={selectedIntegrationData.isMultipleIssues}
              hostProductName={apiHostname}
              integrationData={selectedIntegrationData}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              projectsByConnections={projectsByConnections}
              connectionIdsWithLoadingErrors={connectionIdsWithLoadingErrors}
              loading={isProjectsLoading}
              loadingError={!!projectsError}
              allConnectionsTemplates={
                selectedIntegrationData.allConnectionsTemplates
              }
              templateError={templateError}
            />
          )
        },
        {
          isValid: isProvideValuesStepValid,
          heading: t('Set values'),
          content: (
            <ProvideValues uiFields={uiFields} setUIFields={setUIFields} />
          )
        }
      ]
    : [];

  return {
    steps,
    isProjectsLoading,
    projectsByConnections,
    projectsError,
    connectionIdsWithLoadingErrors
  };
};
